// ---------------------------------------------------------------------------
// Which tiles a typed word uses up.
//
// The letters round never needs this: an answer there is built by tapping, so
// it is already a list of tile indices. The conundrum is a free text box — you
// type a nine letter word — and nothing connected what you typed back to the
// nine tiles in front of you. So a player worked through an anagram with no
// indication of which letters they had already spent, which is the one thing
// the physical version of this puzzle gives you for free.
//
// Matching is by multiset and left to right. A board with two Ns and a word with
// two Ns spends both; a word with three spends the two that exist and reports
// the third as unmatched. Anything else would either mark a tile spent twice or
// leave a real duplicate looking available.
// ---------------------------------------------------------------------------

export interface TileUsage {
  /** Indices into the tile row, in the order the typed letters claimed them. */
  used: number[];
  /**
   * Typed letters with no tile left to match. Non-zero means the word cannot be
   * built from this board, which is worth saying before the player submits it
   * and loses the round to a lockout.
   */
  unmatched: number;
}

/**
 * Spend `typed` against `tiles`.
 *
 * Case and whitespace are the caller's problem to display but not to track:
 * both sides are folded to upper case, and anything that is not a letter is
 * ignored rather than counted as unmatched, so a stray space is not an error.
 */
export function consumeTiles(tiles: string[], typed: string): TileUsage {
  const board = tiles.map((tile) => tile.toUpperCase());
  const taken = new Set<number>();
  const used: number[] = [];
  let unmatched = 0;

  for (const character of typed.toUpperCase()) {
    if (!/[A-Z]/.test(character)) continue;

    const at = board.findIndex((tile, index) => !taken.has(index) && tile === character);
    if (at === -1) {
      unmatched += 1;
      continue;
    }
    taken.add(at);
    used.push(at);
  }

  return { used, unmatched };
}
